
import { Component, Prop, VModel, Vue, Watch } from "vue-property-decorator";
import axios from "axios";
import { Factcheck } from "@/types";
import * as FactcheckServices from "../../services/FactCheckService";
@Component
export default class FactcheckForm extends Vue {
  @VModel() factcheck!: Factcheck;
  @Prop({ required: false, default: false }) isNew!: boolean;
  @Prop() prefilledMetadata!: Record<
    string,
    {
      confidence: string;
      sourceQuote: string;
      originalUrl: string;
    }
  >;

  types = ["Factcheck", "Debunk", "Narrative", "Prebunk"];

  customErrors = [] as string[];

  private debounceTimeout: number | null = null;

  @Watch("factcheck.title")
  onTitleChange() {
    if (!this.isNew) {
      return;
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
    this.debounceTimeout = window.setTimeout(() => {
      this.checkTitleAvailable(this.factcheck.title ?? "");
    }, 400);
  }

  // Same slug rules as GenerateId in the API: the slug is the factcheck id.
  private toSlug(title: string): string {
    return title
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
  }

  private async checkTitleAvailable(title: string) {
    const slug = this.toSlug(title);
    if (!slug) {
      this.customErrors = [];
      return;
    }

    let taken = false;
    try {
      taken = (await FactcheckServices.GetFactCheck(slug)) !== null;
    } catch (error) {
      // 404 means the slug is free. Any other failure: don't block on a guess.
      if (!axios.isAxiosError(error) || error.response?.status !== 404) {
        console.error("Title check failed", error);
      }
    }

    // A newer title was typed while this request ran: drop the stale answer.
    if (this.toSlug(this.factcheck.title ?? "") !== slug) {
      return;
    }

    this.customErrors = taken
      ? ["Er bestaat al een factcheck met deze titel. Kies een andere titel."]
      : [];
  }

  // Helper method to truncate quotes for display
  truncateQuote(quote: string, maxLength = 100): string {
    if (!quote || quote.length <= maxLength) {
      return quote;
    }
    return quote.substring(0, maxLength) + "...";
  }

  // Helper method to get color based on confidence level
  getConfidenceColor(confidence: string): string {
    switch (confidence.toLowerCase()) {
      case 'high':
        return 'success';
      case 'medium':
        return 'warning';
      case 'low':
        return 'error';
      default:
        return 'info';
    }
  }

  // Helper method to emit source quote data to parent
  openSourceQuote(fieldName: string) {
    if (this.prefilledMetadata && this.prefilledMetadata[fieldName]) {
      const metadata = this.prefilledMetadata[fieldName];
      this.$emit('open-source-quote', {
        url: metadata.originalUrl,
        text: metadata.sourceQuote
      });
    }
  }
}
